import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Chip, Link } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { PaginationFilterType } from '@types';
import { DynamicCard, Table } from '@components';
const money = (value) => (value ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const EMPTY = { processos: [], totais: { cobradoLocal: 0, porFaturar: 0, faturado: 0, count: 0 } };
const estadoColor = (estado) => {
    if (estado === 'Faturado')
        return 'success';
    if (estado === 'Por faturar')
        return 'warning';
    return 'default';
};
const GasMapaFaturacaoView = ({ $state, GasService, UIService }) => {
    const [from, setFrom] = useState(() => ($state.params.from ? moment($state.params.from) : moment().startOf('month')));
    const [to, setTo] = useState(() => ($state.params.to ? moment($state.params.to) : moment()));
    const [loading, setLoading] = useState(false);
    const [raw, setRaw] = useState(EMPTY);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
    const [sort, setSort] = useState([]);
    const [filter, setFilter] = useState([]);
    const fetchData = async () => {
        if (!from || !to || !from.isValid() || !to.isValid())
            return;
        setLoading(true);
        try {
            const result = await GasService.getMapaFaturacao(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'));
            setRaw(result ?? EMPTY);
            $state.go($state.current.name, { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }, { notify: false, reload: false, location: 'replace', inherit: true });
        }
        catch (e) {
            console.log(e);
            UIService.addToast('Ocorreu um erro ao carregar o mapa de faturação.');
        }
        setLoading(false);
    };
    useEffect(() => {
        setPagination(p => ({ ...p, pageIndex: 0 }));
        fetchData();
    }, [from, to]);
    const columns = useMemo(() => {
        const moneyCell = ({ cell }) => <span>{money(cell.getValue() ?? 0)}</span>;
        const dateCell = ({ cell }) => <span>{cell.getValue() ? moment(cell.getValue()).format('DD/MM/YYYY') : '—'}</span>;
        const alignRight = { muiTableHeadCellProps: { align: 'right' }, muiTableBodyCellProps: { align: 'right' } };
        return [
            { accessorKey: 'numeroProcesso', header: 'Nº Processo', size: 110 },
            { accessorKey: 'numeroParceiro', header: 'Nº Parceiro', size: 110 },
            { accessorKey: 'data', header: 'Data Inspeção', size: 110, Cell: dateCell },
            { accessorKey: 'tecnicoNome', header: 'Técnico', size: 150 },
            { accessorKey: 'distrito', header: 'Distrito', size: 100 },
            { accessorKey: 'tipoInstalacao', header: 'Tipo Instalação', size: 120 },
            { accessorKey: 'cobradoLocal', header: 'Cobrado no local', Cell: moneyCell, ...alignRight },
            { accessorKey: 'totalProcesso', header: 'Total do Processo', Cell: moneyCell, ...alignRight },
            {
                accessorKey: 'estadoFaturacao',
                header: 'Estado Faturação',
                size: 140,
                Cell: ({ cell }) => <Chip size='small' label={cell.getValue()} color={estadoColor(cell.getValue())}/>
            },
            { accessorKey: 'tipoFatura', header: 'Tipo Fatura', size: 110 },
            { accessorKey: 'numeroFatura', header: 'Nº Fatura', size: 110 },
            { accessorKey: 'dataFatura', header: 'Data Fatura', size: 110, Cell: dateCell }
        ];
    }, []);
    const options = useMemo(() => ({
        filter: [
            { name: 'numeroProcesso', type: PaginationFilterType.TEXT },
            { name: 'numeroParceiro', type: PaginationFilterType.TEXT },
            { name: 'tecnicoNome', type: PaginationFilterType.TEXT },
            { name: 'distrito', type: PaginationFilterType.TEXT },
            { name: 'tipoInstalacao', type: PaginationFilterType.TEXT },
            { name: 'cobradoLocal', type: PaginationFilterType.NUMBER },
            { name: 'totalProcesso', type: PaginationFilterType.NUMBER },
            { name: 'estadoFaturacao', type: PaginationFilterType.TEXT },
            { name: 'tipoFatura', type: PaginationFilterType.TEXT },
            { name: 'numeroFatura', type: PaginationFilterType.TEXT }
        ],
        sort: [
            'numeroProcesso',
            'numeroParceiro',
            'data',
            'tecnicoNome',
            'distrito',
            'tipoInstalacao',
            'cobradoLocal',
            'totalProcesso',
            'estadoFaturacao',
            'tipoFatura',
            'numeroFatura'
        ]
    }), []);
    const data = useMemo(() => {
        let rows = [...raw.processos];
        filter.forEach(f => {
            const val = String(f.value ?? '').toLowerCase().trim();
            if (!val)
                return;
            rows = rows.filter(r => {
                const cell = r[f.id];
                if (cell == null)
                    return false;
                return String(cell).toLowerCase().includes(val);
            });
        });
        if (sort.length) {
            const s = sort[0];
            rows = [...rows].sort((a, b) => {
                const av = a[s.id];
                const bv = b[s.id];
                let c;
                if (typeof av === 'number' && typeof bv === 'number')
                    c = av - bv;
                else
                    c = String(av ?? '').localeCompare(String(bv ?? ''));
                return s.desc ? -c : c;
            });
        }
        const totalItems = rows.length;
        const pageSize = pagination.pageSize || 20;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
        const start = pagination.pageIndex * pageSize;
        const pageRows = rows.slice(start, start + pageSize);
        return {
            data: pageRows,
            meta: { currentPage: pagination.pageIndex + 1, itemsPerPage: pageSize, totalItems, totalPages },
            links: {}
        };
    }, [raw, filter, sort, pagination]);
    const exportXlsx = () => {
        if (!raw.processos.length) {
            UIService.addToast('Sem dados para exportar.');
            return;
        }
        const header = [
            'Nº Processo',
            'Nº Parceiro',
            'Data Inspeção',
            'Técnico',
            'Distrito',
            'Tipo Instalação',
            'Cobrado no local',
            'Total do Processo',
            'Estado Faturação',
            'Tipo Fatura',
            'Nº Fatura',
            'Data Fatura'
        ];
        const body = raw.processos.map(p => [
            p.numeroProcesso,
            p.numeroParceiro,
            p.data ? moment(p.data).format('DD/MM/YYYY') : '',
            p.tecnicoNome ?? '',
            p.distrito ?? '',
            p.tipoInstalacao ?? '',
            p.cobradoLocal,
            p.totalProcesso,
            p.estadoFaturacao,
            p.tipoFatura ?? '',
            p.numeroFatura ?? '',
            p.dataFatura ? moment(p.dataFatura).format('DD/MM/YYYY') : ''
        ]);
        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Mapa Faturação');
        XLSX.writeFile(wb, `gas-mapa-faturacao-${from.format('YYYY-MM-DD')}-${to.format('YYYY-MM-DD')}.xlsx`);
    };
    const resumoContent = useMemo(() => [
        { label: 'Cobrado no local:', value: money(raw.totais.cobradoLocal) },
        { label: 'Por faturar:', value: money(raw.totais.porFaturar) },
        { label: 'Faturado:', value: money(raw.totais.faturado) },
        { label: 'Nº de processos:', value: String(raw.totais.count) }
    ], [raw]);
    return (<Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Breadcrumbs aria-label='breadcrumb'>
          <Link underline='none' color='inherit'>
            Gás
          </Link>
          <Link underline='hover' color='text.primary'>
            Mapa Geral de Faturação
          </Link>
        </Breadcrumbs>
      </Box>

      <Grid container spacing={1}>
        <Grid xs={12} md={6}>
          <DynamicCard title='Filtros' sx={{ height: '100%' }} content={[
            {
                label: 'De:',
                value: (<LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker format='DD/MM/YYYY' value={from} onChange={value => value && setFrom(value)} slotProps={{ textField: { variant: 'outlined', fullWidth: true, size: 'small' } }}/>
                  </LocalizationProvider>)
            },
            {
                label: 'Até:',
                value: (<LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker format='DD/MM/YYYY' value={to} onChange={value => value && setTo(value)} slotProps={{ textField: { variant: 'outlined', fullWidth: true, size: 'small' } }}/>
                  </LocalizationProvider>)
            }
        ]} loading={false}/>
        </Grid>
        <Grid xs={12} md={6}>
          <DynamicCard title='Resumo' sx={{ height: '100%' }} content={resumoContent} loading={false}/>
        </Grid>

        <Grid xs={12} md={12}>
          <Table storageKey={$state.current.name} data={data} columns={columns} initial={{ sort, filter }} isLoading={loading} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} onExport={exportXlsx}/>
        </Grid>
      </Grid>
    </Box>);
};
export default GasMapaFaturacaoView;
